import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import ContractorPicker from '../components/ContractorPicker';
import { useSite } from '../context/SiteContext';
import { severityToLabel } from '../utils/severity';
import { allowedTransitions } from '../constants/incidentStates';
import { INCIDENT_TYPE_HELP } from '../constants/incidentTypes';
import { todayLocalISO } from '../utils/dateUtils';
import { useTenantFieldConfig, stripHiddenRcaFields } from '../hooks/useTenantFieldConfig';
import { usePermissions } from '../context/PermissionContext';
import { canAssignOnReport } from '../utils/roleUtils';

/**
 * Put the contractor attribution on the wire correctly for the operation.
 *
 * The two cases genuinely differ, which is why both submit paths must call
 * this rather than each rolling its own:
 *
 *  CREATE — omit an empty value entirely, so the foreign key stays NULL. An
 *  empty string is not a contractor and would still satisfy IS NOT NULL.
 *
 *  EDIT — send an explicit '' for an empty value. The PUT reads an omitted key
 *  as "leave unchanged", so omitting it would make REMOVING an attribution
 *  impossible; the server turns '' back into NULL.
 *
 */
function applyContractorAttribution(body, isEditing) {
  const company = (body.contractor_company_id || '').trim();
  if (company) body.contractor_company_id = company;
  else if (isEditing) body.contractor_company_id = '';
  else delete body.contractor_company_id;
}

/**
 * S2-29: upload an incident's photos and documents after the incident itself
 * was saved. Each endpoint takes the whole batch, so a failure means none of
 * that batch was attached. Never throws: returns the files that did NOT make
 * it (and the server's reason) so the caller can tell the user and offer a
 * retry against the same incident id, instead of a plain "success".
 */
export async function uploadIncidentAttachments(incidentId, photos = [], documents = [], post = axios.post) {
  const failed = { photos: [], documents: [], reason: '' };
  const reasonOf = (err) => {
    const detail = err?.response?.data?.detail;
    return typeof detail === 'string' ? detail : '';
  };
  if (photos.length > 0 && incidentId) {
    try {
      const fd = new FormData();
      photos.forEach(file => fd.append('files', file));
      await post(`${API}/incidents/${incidentId}/upload-photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (err) {
      console.error('Photo upload error:', err?.response?.data);
      failed.photos = photos;
      failed.reason = failed.reason || reasonOf(err);
    }
  }
  if (documents.length > 0 && incidentId) {
    try {
      const fd = new FormData();
      documents.forEach(file => fd.append('files', file));
      await post(`${API}/incidents/${incidentId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (err) {
      console.error('Document upload error:', err?.response?.data);
      failed.documents = documents;
      failed.reason = failed.reason || reasonOf(err);
    }
  }
  return failed;
}

export function describeFailedAttachments(failure) {
  const parts = [];
  if (failure.photos?.length) parts.push(`${failure.photos.length} photo(s)`);
  if (failure.documents?.length) parts.push(`${failure.documents.length} document(s)`);
  return parts.join(' and ');
}

/**
 * S2-29: shown instead of the success screen when the incident was saved but
 * some attachments were not. Retry re-uploads only the failed files.
 */
export function AttachmentUploadFailure({ failure, isEditing, retrying, onRetry, onContinue }) {
  return (
    <div className="max-w-xl mx-auto bg-white rounded-xl shadow-md p-8 mt-12" data-testid="attachment-upload-failure" role="alert">
      <h2 className="text-xl font-bold text-gray-800 mb-2">
        {isEditing ? 'Incident updated' : 'Incident saved'}, but {describeFailedAttachments(failure)} could not be attached
      </h2>
      {failure.reason && (
        <p className="text-sm text-red-700 bg-red-50 rounded p-2 mb-3">{failure.reason}</p>
      )}
      <ul className="text-sm text-gray-700 list-disc pl-5 mb-4">
        {[...(failure.photos || []), ...(failure.documents || [])].map((f, i) => (
          <li key={`${f.name}-${i}`}>{f.name}</li>
        ))}
      </ul>
      <p className="text-sm text-gray-600 mb-6">
        The incident itself is saved. Retry the upload, or continue and add the files later from the incident page.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="px-5 py-2 bg-[#1B5C94] text-white rounded-lg font-semibold hover:bg-[#164a7a] disabled:bg-gray-300"
        >
          {retrying ? 'Retrying…' : 'Retry upload'}
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={retrying}
          className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Continue without them
        </button>
      </div>
    </div>
  );
}

const ReportHazard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When set, the form acts as an edit screen for a previously saved
  // incident (any non-Closed status) and PUTs the changes onto the existing
  // record instead of POSTing a new one. The query param keeps the legacy
  // `draftId` name so existing links keep working.
  const draftId = searchParams.get('draftId');
  const isEditing = Boolean(draftId);
  // CMP-27: only users who can actually create (or, when editing, update)
  // incidents get the form. Resource-specific check via getResourcePermissions:
  // hasPermission() also matches module_slug and would let a read-only user in.
  // The API enforces the same rule; this only avoids a form that fails on submit.
  const { getResourcePermissions, loading: permissionsLoading } = usePermissions();
  const requiredIncidentAction = isEditing ? 'update' : 'create';
  const canUseIncidentForm = getResourcePermissions('incidents')
    .some(p => p.action === requiredIncidentAction);
  // Tenant-configured visibility of Root Cause / Corrective Action /
  // Recommended Future Actions (all shown unless the tenant switched one off).
  const rcaFields = useTenantFieldConfig();
  const anyRcaFieldShown = rcaFields.root_cause || rcaFields.corrective_action || rcaFields.recommended_actions;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // S2-29: { incidentId, dest, photos, documents, reason } when the incident
  // saved but some attachments did not.
  const [uploadFailure, setUploadFailure] = useState(null);
  const [retryingUpload, setRetryingUpload] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]); // Store actual files for S3 upload
  const [photoPreviews, setPhotoPreviews] = useState([]); // Store base64 for preview only
  // Photos already attached to the incident (only populated when editing
  // an existing draft via ?draftId). Shape: [{ url, object_key }, ...].
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [existingPhotosLoading, setExistingPhotosLoading] = useState(false);
  const [deletingPhotoKey, setDeletingPhotoKey] = useState(null);
  // Documents — mirror of the photos pattern. Files chosen during create/
  // edit are buffered locally and POSTed after the incident exists, so
  // documents work identically in Create and Edit.
  const [documentFiles, setDocumentFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [existingDocumentsLoading, setExistingDocumentsLoading] = useState(false);
  const [deletingDocIndex, setDeletingDocIndex] = useState(null);
  const [users, setUsers] = useState([]);
  const [draftLoading, setDraftLoading] = useState(isEditing);

  const { activeSiteId, activeSiteName } = useSite();
  // Admin, HSE Manager and Safety Officer may pick the assignee.
  const canAssign = canAssignOnReport(user);

  const ROOT_CAUSE_TAGS = [
    'Equipment', 'PPE', 'Training', 'Procedures', 'Human Factors',
    'Environmental', 'Housekeeping', 'Communication', 'Supervision',
    'Maintenance', 'Design', 'Management System'
  ];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    hazard_type: '',
    severity: '',
    // Deliberately blank — the reporter must state when the incident actually
    // occurred rather than accepting today's date/time. The edit-load below
    // overwrites both with the saved values for an existing record.
    incident_date: '',
    incident_time: '',
    location: '',
    department_id: '',
    // Contractor attribution — optional, and null for most incidents.
    contractor_company_id: '',
    equipment_involved: '',
    immediate_actions: '',
    root_cause: '',
    corrective_action: '',
    recommended_actions: '',
    is_lti: false,
    medical_leave_days: '',
    injury_count: '',
    fatality_count: '',
    // Per-person LTI days, one entry per injured person. Length tracks
    // injury_count via a useEffect below; each entry is {lti_days: number}.
    injured_persons: [],
    assignee: '',
    site_id: '',
    site_name: '',
    tags: [],
    // Edit-mode-only fields. Populated from the loaded incident when editing
    // an existing record. Hidden in the create flow.
    status: '',
    target_date: '',
    due_date: '',
  });

  // Status the incident currently has on the server. Used to decide whether
  // SUBMIT promotes a Draft to Open or just saves with the existing status.
  const [loadedStatus, setLoadedStatus] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [departments, setDepartments] = useState([]);

  // Load the tenant department master for the Department picker.
  useEffect(() => {
    axios.get(`${API}/departments`)
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  // Assignee options: only the Safety Managers who own the selected
  // department, so a reporter cannot pick a manager from another department.
  // Refetched whenever the department changes; cleared when none is chosen.
  useEffect(() => {
    if (!canAssign || !formData.department_id) {
      setUsers([]);
      return;
    }
    const params = new URLSearchParams({ department_id: formData.department_id });
    if (activeSiteId) params.append('site_id', activeSiteId);
    axios.get(`${API}/incidents/eligible-assignees?${params.toString()}`)
      .then(res => setUsers(res.data || []))
      .catch(err => {
        console.error('Error fetching eligible assignees:', err);
        setUsers([]);
      });
  }, [canAssign, activeSiteId, formData.department_id]);

  // Auto-set site from active site context (global site switcher).
  // Skipped while editing a draft so the draft remains tied to the site it
  // was created for (SOC 2 CC6.1 — site-scoped tenant isolation).
  useEffect(() => {
    if (isEditing) return;
    if (activeSiteId) {
      setFormData(prev => ({ ...prev, site_id: activeSiteId, site_name: activeSiteName || '' }));
    } else {
      setFormData(prev => ({ ...prev, site_id: '', site_name: '' }));
    }
  }, [activeSiteId, activeSiteName, isEditing]);

  // Load existing incident when ?draftId is present so the form opens
  // prepopulated. Same param works for any status — the URL keeps the legacy
  // name but the form handles Draft, Open, In-Progress, etc.
  const loadIncident = async () => {
    setDraftLoading(true);
    try {
      const response = await axios.get(`${API}/incidents/${draftId}`);
      const data = response.data || {};
      const recommended = Array.isArray(data.recommended_actions)
        ? data.recommended_actions.join('; ')
        : (data.recommended_actions || '');
      setFormData(prev => ({
        ...prev,
        title: data.title || '',
        description: data.description || '',
        hazard_type: data.hazard_type || '',
        severity: severityToLabel(data.severity) || '',
        incident_date: data.incident_date ? String(data.incident_date).split('T')[0] : '',
        // Saved time wins over the new-report prefill. Falls back to blank
        // (not "now") so an older record without a time stays honest.
        incident_time: data.incident_time || '',
        location: data.location || '',
        department_id: data.department_id || '',
        contractor_company_id: data.contractor_company_id || '',
        equipment_involved: data.equipment_involved || '',
        immediate_actions: data.immediate_actions || '',
        root_cause: data.root_cause || '',
        corrective_action: data.corrective_action || '',
        recommended_actions: recommended,
        is_lti: Boolean(data.is_lti),
        medical_leave_days: data.medical_leave_days != null ? String(data.medical_leave_days) : '',
        injury_count: data.injury_count != null ? String(data.injury_count) : '',
        fatality_count: data.fatality_count != null ? String(data.fatality_count) : '',
        injured_persons: Array.isArray(data.injured_persons)
          ? data.injured_persons.map(p => ({ lti_days: Number(p?.lti_days) || 0 }))
          : [],
        assignee: data.assignee || '',
        site_id: data.site_id || '',
        site_name: data.site_name || '',
        tags: Array.isArray(data.tags) ? data.tags : [],
        status: data.status || '',
        target_date: data.target_date ? String(data.target_date).split('T')[0] : '',
        due_date: data.due_date ? String(data.due_date).split('T')[0] : '',
      }));
      setLoadedStatus(data.status || '');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load incident');
    } finally {
      setDraftLoading(false);
    }
  };

  // Pull photos already attached to the incident so the edit form can show
  // them (the upload widget below only previews newly-selected files).
  // Without this, users opening Edit see no evidence of photos they
  // previously uploaded.
  const loadExistingPhotos = async () => {
    setExistingPhotosLoading(true);
    try {
      const response = await axios.get(`${API}/incidents/${draftId}/photos`);
      const photos = Array.isArray(response.data?.photos) ? response.data.photos : [];
      setExistingPhotos(photos.map(p => ({ url: p.photo_url, object_key: p.object_key })));
    } catch (err) {
      // 404 simply means no photos — not an error worth surfacing
      if (err.response?.status !== 404) {
        console.error('Failed to fetch existing photos:', err);
      }
      setExistingPhotos([]);
    } finally {
      setExistingPhotosLoading(false);
    }
  };

  const handleDeleteExistingPhoto = async (objectKey) => {
    if (!objectKey) return;
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    setDeletingPhotoKey(objectKey);
    try {
      await axios.delete(`${API}/incidents/${draftId}/photos`, {
        data: { object_key: objectKey },
      });
      await loadExistingPhotos();
    } catch (err) {
      console.error('Photo delete error:', err);
      window.alert(err.response?.data?.detail || 'Failed to delete photo');
    } finally {
      setDeletingPhotoKey(null);
    }
  };

  // Pull existing documents so the edit form mirrors the Detail view's
  // Documents & Reports section. Mirrors loadExistingPhotos.
  const loadExistingDocuments = async () => {
    setExistingDocumentsLoading(true);
    try {
      const response = await axios.get(`${API}/incidents/${draftId}/documents`);
      const docs = Array.isArray(response.data?.documents) ? response.data.documents : [];
      setExistingDocuments(docs);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Failed to fetch existing documents:', err);
      }
      setExistingDocuments([]);
    } finally {
      setExistingDocumentsLoading(false);
    }
  };

  const removePendingDocument = (index) => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingDocument = async (docIndex) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    setDeletingDocIndex(docIndex);
    try {
      await axios.delete(`${API}/incidents/${draftId}/documents/${docIndex}`);
      await loadExistingDocuments();
    } catch (err) {
      console.error('Document delete error:', err);
      window.alert(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeletingDocIndex(null);
    }
  };

  useEffect(() => {
    if (!draftId) return;
    loadIncident();
    loadExistingPhotos();
    loadExistingDocuments();
  }, [draftId]);

  // Keep injured_persons array length in sync with injury_count. Adding rows
  // appends {lti_days: 0}; reducing truncates from the end (confirm if any
  // truncated entry had a non-zero value so the user doesn't silently lose
  // data). Also keeps medical_leave_days mirrored to SUM(lti_days).
  useEffect(() => {
    setFormData(prev => {
      const n = parseInt(prev.injury_count) || 0;
      const current = Array.isArray(prev.injured_persons) ? prev.injured_persons : [];
      let next = current;
      if (n > current.length) {
        next = [...current, ...Array.from({ length: n - current.length }, () => ({ lti_days: 0 }))];
      } else if (n < current.length) {
        const dropped = current.slice(n);
        const hasData = dropped.some(p => Number(p?.lti_days) > 0);
        if (hasData && !window.confirm(
          `Reducing the injury count will drop ${dropped.length} recorded LTI entr${dropped.length === 1 ? 'y' : 'ies'}. Continue?`
        )) {
          // Revert — bump the count back up so the UI matches what's kept.
          return { ...prev, injury_count: String(current.length) };
        }
        next = current.slice(0, n);
      }
      const total = next.reduce((s, p) => s + (Number(p?.lti_days) || 0), 0);
      return {
        ...prev,
        injured_persons: next,
        // Only auto-write medical_leave_days when per-person rows exist; an
        // empty list means the user can still type a total directly.
        medical_leave_days: next.length > 0 ? String(total) : prev.medical_leave_days,
      };
    });
  }, [formData.injury_count]);

  const handleInjuredPersonChange = (index, value) => {
    setFormData(prev => {
      const next = (prev.injured_persons || []).map((p, i) =>
        i === index ? { ...p, lti_days: value } : p
      );
      const total = next.reduce((s, p) => s + (Number(p?.lti_days) || 0), 0);
      return {
        ...prev,
        injured_persons: next,
        medical_leave_days: next.length > 0 ? String(total) : prev.medical_leave_days,
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Keep is_lti mirrored to the incident type so a stale flag can't
    // require the (hidden) Medical Leave Days field after switching away.
    if (name === 'hazard_type') {
      setFormData(prev => ({ ...prev, [name]: value, is_lti: value === 'Lost Time Injury' }));
      return;
    }
    // Changing the department invalidates a previously picked assignee — the
    // eligible list is department-scoped, so a stale cross-department Safety
    // Manager must not survive the switch.
    if (name === 'department_id') {
      setFormData(prev => ({ ...prev, [name]: value, assignee: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Single attachment picker handler — auto-routes by extension. Images
  // (jpg/jpeg/png/gif) go through the photo upload pipeline (separate S3
  // path, 10MB cap, 10-file cap, base64 preview); everything else goes
  // through the document upload pipeline (25MB backend cap, 20-file cap).
  const _IMAGE_RE = /\.(jpe?g|png|gif)$/i;

  const handleAttachmentSelect = (e) => {
    const all = Array.from(e.target.files || []);
    if (all.length === 0) return;
    e.target.value = ''; // allow re-selecting the same file after a Remove

    const images = all.filter(f => _IMAGE_RE.test(f.name));
    const docs = all.filter(f => !_IMAGE_RE.test(f.name));

    // Cap checks first so the user gets a single clear error message.
    if (existingPhotos.length + photoFiles.length + images.length > 10) {
      setError(`Maximum 10 photos allowed (${existingPhotos.length} attached, ${photoFiles.length} pending)`);
      return;
    }
    if (existingDocuments.length + documentFiles.length + docs.length > 20) {
      setError(`Maximum 20 documents allowed (${existingDocuments.length} attached, ${documentFiles.length} pending)`);
      return;
    }

    // Image branch — per-file size check + preview generation.
    const validImages = [];
    let imageError = false;
    images.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`${file.name}: Image size should be less than 10MB`);
        imageError = true;
        return;
      }
      validImages.push(file);
    });
    validImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews(prev => [...prev, { name: file.name, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
    if (validImages.length > 0) {
      setPhotoFiles(prev => [...prev, ...validImages]);
    }

    // Document branch — backend enforces size + magic-bytes; just append.
    if (docs.length > 0) {
      setDocumentFiles(prev => [...prev, ...docs]);
    }

    if (!imageError) setError('');
  };

  const removePhoto = (index) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerateAIAnalysis = async () => {
    if (!isEditing) return;
    setError('');
    setGeneratingAI(true);
    try {
      await axios.post(`${API}/incidents/${draftId}/generate-ai-analysis`);
      // Pull the updated incident so the freshly generated fields populate
      // the form in place.
      await loadIncident();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate AI analysis');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!formData.site_id) {
      setError('Please select a site from the header first.');
      return;
    }
    if (!formData.title || !formData.title.trim()) {
      setError('Title is required to save as draft');
      return;
    }
    setError('');
    setLoading(true);

    try {
      // Hidden RCA fields are never sent (the backend drops them too).
      const submissionData = stripHiddenRcaFields({
        ...formData,
        status: 'Draft',
        description: formData.description || '',
      }, rcaFields);
      if (!submissionData.assignee) delete submissionData.assignee;
      if (!submissionData.department_id) delete submissionData.department_id;
      // On CREATE send nothing rather than '' so an unattributed incident
      // leaves the FK NULL. On EDIT send an explicit '' — an omitted key means
      // "leave unchanged" on the PUT, which would make REMOVING a contractor
      // impossible; the server reads '' as a clear.
      applyContractorAttribution(submissionData, isEditing);
      // incident_date is optional for drafts. Pydantic rejects "" as a
      // datetime, so drop the key when blank instead of sending an empty string.
      if (!submissionData.incident_date) delete submissionData.incident_date;
      // incident_time is always optional. On CREATE, drop a blank value so the
      // column stays NULL. On EDIT, send an explicit '' — the PUT handler reads
      // "" as "clear this field", whereas an omitted key means "leave
      // unchanged", which would make a cleared time impossible to save.
      if (!submissionData.incident_time) {
        if (isEditing) submissionData.incident_time = '';
        else delete submissionData.incident_time;
      }
      if (!submissionData.target_date) delete submissionData.target_date;
      if (!submissionData.due_date) delete submissionData.due_date;
      // Convert numeric fields to numbers or null for backend validation
      if (submissionData.medical_leave_days === '' || submissionData.medical_leave_days === undefined) {
        submissionData.medical_leave_days = null;
      } else if (submissionData.medical_leave_days) {
        submissionData.medical_leave_days = parseFloat(submissionData.medical_leave_days);
      }
      submissionData.injury_count = submissionData.injury_count ? parseInt(submissionData.injury_count) : 0;
      submissionData.fatality_count = submissionData.fatality_count ? parseInt(submissionData.fatality_count) : 0;
      if (submissionData.injury_count > 0 && Array.isArray(submissionData.injured_persons)) {
        submissionData.injured_persons = submissionData.injured_persons.map((p, i) => ({
          person_index: i,
          lti_days: Number(p?.lti_days) || 0,
        }));
      } else {
        delete submissionData.injured_persons;
      }
      const response = isEditing
        ? await axios.put(`${API}/incidents/${draftId}`, submissionData)
        : await axios.post(`${API}/incidents`, submissionData);
      const incidentId = isEditing ? draftId : response.data.id;
      // After saving an edit go to the detail view; after creating a new
      // draft go to the incidents list.
      const dest = isEditing ? `/incidents/${incidentId}` : '/incidents';

      // S2-29: a failed attachment upload is reported (with a retry) rather
      // than swallowed behind the success screen.
      const failed = await uploadIncidentAttachments(incidentId, photoFiles, documentFiles);
      if (failed.photos.length || failed.documents.length) {
        setUploadFailure({ incidentId, dest, ...failed });
        return;
      }

      setSuccess(true);
      setTimeout(() => navigate(dest), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSubmitConfirm(true);
  };

  const doSubmit = async () => {
    setShowSubmitConfirm(false);
    if (!formData.site_id) {
      setError('Please select a site from the header first.');
      return;
    }
    // Department drives assignment routing, so a submitted incident must have
    // one. Draft-save stays lenient by design (title is its only requirement).
    if (!formData.department_id) {
      setError('Department is required — it determines which Safety Manager the incident is assigned to.');
      return;
    }
    setError('');
    setLoading(true);
    setAnalyzing(true);

    try {
      // Clean up form data before submission. Hidden RCA fields are never
      // sent (the backend drops them too).
      const submissionData = stripHiddenRcaFields(formData, rcaFields);

      // Send assignee only if selected, otherwise let AI assign
      if (!submissionData.assignee) {
        delete submissionData.assignee;
      }
      if (!submissionData.department_id) delete submissionData.department_id;
      // Empty optional dates fail Pydantic datetime parsing; drop them.
      if (!submissionData.target_date) delete submissionData.target_date;
      if (!submissionData.due_date) delete submissionData.due_date;
      // incident_time is never required (unlike incident_date). See the draft
      // path above for why EDIT sends '' while CREATE omits the key.
      if (!submissionData.incident_time) {
        if (isEditing) submissionData.incident_time = '';
        else delete submissionData.incident_time;
      }

      // is_lti follows the incident type (covers stale flags on loaded drafts)
      submissionData.is_lti = submissionData.hazard_type === 'Lost Time Injury';

      // Validate LTI fields when required
      const isLtiRequired = submissionData.hazard_type === 'Lost Time Injury';
      if (isLtiRequired && (!submissionData.medical_leave_days || submissionData.medical_leave_days === '')) {
        setError('Medical Leave Days is required when Lost Time Injury is selected');
        setLoading(false);
        setAnalyzing(false);
        return;
      }

      // Convert medical_leave_days to number or null
      if (submissionData.medical_leave_days === '' || !isLtiRequired) {
        submissionData.medical_leave_days = null;
      } else if (submissionData.medical_leave_days) {
        submissionData.medical_leave_days = parseFloat(submissionData.medical_leave_days);
      }

      applyContractorAttribution(submissionData, isEditing);

      // Convert injury_count and fatality_count to integers
      submissionData.injury_count = submissionData.injury_count ? parseInt(submissionData.injury_count) : 0;
      submissionData.fatality_count = submissionData.fatality_count ? parseInt(submissionData.fatality_count) : 0;

      // Per-person LTI array: only sent when there's at least one injured
      // person. The server derives medical_leave_days = SUM(lti_days).
      if (submissionData.injury_count > 0 && Array.isArray(submissionData.injured_persons)) {
        submissionData.injured_persons = submissionData.injured_persons.map((p, i) => ({
          person_index: i,
          lti_days: Number(p?.lti_days) || 0,
        }));
      } else {
        delete submissionData.injured_persons;
      }

      // PUT when editing an existing incident; POST when creating new.
      // Submit semantics:
      //   - Editing a Draft → promote to 'Open'
      //   - Editing a non-Draft → keep whatever status the form shows
      //   - Creating new → backend default ('Open' via LLM path)
      let response;
      let incidentId;
      if (isEditing) {
        if (loadedStatus === 'Draft') {
          submissionData.status = 'Open';
        }
        // incident_date is required for a submitted (non-draft) report — drop
        // empty values to surface a clean 400 from the form-level required.
        if (!submissionData.incident_date) delete submissionData.incident_date;
        response = await axios.put(`${API}/incidents/${draftId}`, submissionData);
        incidentId = draftId;
      } else {
        response = await axios.post(`${API}/incidents`, submissionData);
        incidentId = response.data.id;
      }

      const dest = isEditing ? `/incidents/${incidentId}` : '/incidents';

      // Upload photos/documents. A failure doesn't undo the saved incident,
      // but S2-29: it is no longer hidden behind "Reported Successfully!" —
      // the user is told what didn't attach and can retry.
      const failed = await uploadIncidentAttachments(incidentId, photoFiles, documentFiles);
      if (failed.photos.length || failed.documents.length) {
        setUploadFailure({ incidentId, dest, ...failed });
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(dest);
      }, 2000);
    } catch (err) {
      console.error('Incident submission error:', err.response?.data);

      // Handle validation errors from backend
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;

        // If detail is an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(e => `${e.loc?.join(' > ') || 'Field'}: ${e.msg}`).join(', ');
          setError(errorMessages);
        }
        // If detail is a string
        else if (typeof detail === 'string') {
          setError(detail);
        }
        // If detail is an object
        else {
          setError(JSON.stringify(detail));
        }
      } else {
        setError('Failed to submit incident report');
      }
    } finally {
      setLoading(false);
      setAnalyzing(false);
    }
  };

  // S2-29: retry only the attachments that failed, against the saved incident.
  const retryFailedUploads = async () => {
    if (!uploadFailure) return;
    setRetryingUpload(true);
    try {
      const failed = await uploadIncidentAttachments(
        uploadFailure.incidentId, uploadFailure.photos, uploadFailure.documents,
      );
      if (failed.photos.length || failed.documents.length) {
        setUploadFailure({ ...uploadFailure, ...failed });
        return;
      }
      const { dest } = uploadFailure;
      setUploadFailure(null);
      setSuccess(true);
      setTimeout(() => navigate(dest), 2000);
    } finally {
      setRetryingUpload(false);
    }
  };

  if (uploadFailure) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AttachmentUploadFailure
            failure={uploadFailure}
            isEditing={isEditing}
            retrying={retryingUpload}
            onRetry={retryFailedUploads}
            onContinue={() => navigate(uploadFailure.dest)}
          />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center justify-center h-96" data-testid="success-message">
          <div className="bg-green-100 rounded-full p-6 mb-4">
            <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {isEditing ? 'Incident Updated Successfully!' : 'Incident Reported Successfully!'}
          </h2>
          <p className="text-gray-600">Redirecting to incidents list...</p>
        </div>
      </div>
      </div>
    );
  }

  // CMP-27 / CMP-10: a user without the right permission, or an incident that
  // is already Closed (read-only), gets a clear message instead of a form that
  // the server would refuse on submit.
  if (!permissionsLoading && (!canUseIncidentForm || (isEditing && !draftLoading && loadedStatus === 'Closed'))) {
    const closed = canUseIncidentForm;
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl shadow-md p-8 text-center" data-testid={closed ? 'incident-closed-readonly' : 'report-incident-no-permission'}>
            <h1 className="text-2xl font-bold text-[#1B5C94] mb-2">
              {closed ? 'This incident is closed' : "You don't have permission to report incidents"}
            </h1>
            <p className="text-gray-600 mb-6">
              {closed
                ? 'A closed incident is read-only and can no longer be edited.'
                : (isEditing
                  ? 'Your role does not allow editing incidents. Contact your administrator if you need access.'
                  : 'Your role does not allow reporting incidents. Contact your administrator if you need access.')}
            </p>
            <button
              type="button"
              onClick={() => navigate(closed && draftId ? `/incidents/${draftId}` : '/safety')}
              className="px-6 py-2 bg-[#1B5C94] text-white rounded-lg font-semibold hover:bg-[#164a7a]"
            >
              {closed && draftId ? 'View incident' : 'Back to Safety Hub'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (draftLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-3xl mx-auto" data-testid="report-hazard-page">
        <div className="bg-white rounded-xl shadow-md p-8">
          <button
            type="button"
            onClick={() => navigate('/safety')}
            className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold mb-2 flex items-center"
            data-testid="back-to-safety-button"
          >
            ← Back to Safety Hub
          </button>
          <h1 className="text-3xl font-bold text-[#1B5C94] mb-2">
            {isEditing ? 'EDIT INCIDENT' : 'REPORT NEW INCIDENT'}
          </h1>
          <p className="text-gray-600 mb-2">
            {isEditing
              ? 'Update the previously saved details, then save as draft or submit the report.'
              : 'Fill in the details below to report a workplace incident'}
          </p>
          <div className="bg-blue-50 border-l-4 border-[#1B5C94] p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-[#1B5C94] mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-[#1B5C94]">AI-Assisted Analysis</p>
                {/* Assignee is deliberately no longer listed here: it is routed
                    from the selected Department to that department's Safety
                    Manager, not suggested by the AI. */}
                <p className="text-xs text-gray-700 mt-1">You control the Incident Type and Severity. Our AI can optionally assist with: Equipment Analysis, Root Cause, and Corrective Actions (if you leave those fields blank). The assignee is set from the Department you select.</p>
              </div>
            </div>
          </div>

          {/* Gate the form when no site is active (create mode only). Mirrors
              the HIRA Create flow — incidents are site-scoped, so we refuse to
              render the form until the user has chosen a site, otherwise data
              entered now would be lost on submit. Edit mode is exempt because
              the loaded incident already carries its own site_id. */}
          {!isEditing && !activeSiteId ? (
            <div className="bg-amber-50 rounded-xl p-10 text-center border border-amber-300" data-testid="site-required-prompt">
              <svg className="w-12 h-12 text-amber-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Select a site to continue</h2>
              <p className="text-sm text-gray-600 max-w-md mx-auto">
                Incidents are site-scoped. Use the site selector in the header to
                choose the site this incident applies to. The form will load once
                a site is active.
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Site. In create mode it tracks the header site switcher; in
                edit mode it stays pinned to the incident's original site so
                the user doesn't accidentally re-home the record. */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Site <span className="text-red-500">*</span>
              </label>
              {formData.site_id ? (
                <div className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                  {formData.site_name || activeSiteName || 'Loading...'}
                </div>
              ) : (
                <div className="w-full px-4 py-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
                  Please select a site from the header first.
                </div>
              )}
            </div>

            {/* Incident Title */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
                Incident Title <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="hazard-title-input"
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="Brief title for the incident"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                data-testid="hazard-description-input"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="5"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="Detailed description of the incident. Be specific about what happened, when, and any potential risks."
                required
              />
              <p className="text-xs text-gray-500 mt-1">💡 Tip: Include details like time of occurrence, specific location, and potential impact for better AI analysis.</p>
            </div>

            {/* Incident Type and Severity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="hazard_type">
                  Incident Type <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="hazard-type-select"
                  id="hazard_type"
                  name="hazard_type"
                  value={formData.hazard_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  required
                >
                  <option value="">Select Incident Type</option>
                  {/* Observation / Unsafe Act / Unsafe Condition were removed —
                      they belong to the separate Observations module. */}
                  <option value="Near-Miss">Near-Miss</option>
                  <option value="First Aid">First Aid</option>
                  <option value="Medical Treatment">Medical Treatment</option>
                  <option value="Restricted Work">Restricted Work</option>
                  <option value="Lost Time Injury">Lost Time Injury</option>
                  <option value="Fatality">Fatality</option>
                  <option value="Property Damage">Property Damage</option>
                  <option value="Environmental">Environmental</option>
                  <option value="Equipment Failure">Equipment Failure</option>
                  <option value="Occupational Disease">Occupational Disease</option>
                  <option value="Occupational Poisoning">Occupational Poisoning</option>
                  <option value="Other">Other</option>
                </select>
                {formData.hazard_type && INCIDENT_TYPE_HELP[formData.hazard_type] && (
                  <p className="text-xs text-gray-500 mt-1" data-testid="incident-type-help">
                    {INCIDENT_TYPE_HELP[formData.hazard_type]}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="severity">
                  Severity Level <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="severity-select"
                  id="severity"
                  name="severity"
                  value={formData.severity}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  required
                >
                  <option value="">Select Severity</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </div>

            {/* Lost Time Injury Flag — only shown when the incident type is LTI */}
            {formData.hazard_type === 'Lost Time Injury' && (
              <div className="border-2 rounded-lg p-4 bg-orange-50 border-orange-300">
                <label className="flex items-center">
                  <input
                    data-testid="is-lti-checkbox"
                    id="is_lti"
                    name="is_lti"
                    type="checkbox"
                    checked
                    disabled
                    readOnly
                    className="w-5 h-5 text-[#1B5C94] border-gray-300 rounded focus:ring-[#1B5C94] focus:ring-2 disabled:bg-orange-200"
                  />
                  <span className="ml-3">
                    <span className="block text-sm font-bold text-gray-800">
                      Lost Time Injury (LTI)
                      <span className="text-red-500 ml-1">*</span>
                    </span>
                    <span className="block text-xs text-gray-600 mt-1">
                      Required because Incident Type is "Lost Time Injury"
                    </span>
                  </span>
                </label>

                <div className="mt-3 pt-3 border-t border-orange-300">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="medical_leave_days">
                    Medical Leave Days <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="medical_leave_days"
                    name="medical_leave_days"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.medical_leave_days}
                    onChange={handleInputChange}
                    readOnly={Array.isArray(formData.injured_persons) && formData.injured_persons.length > 0}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent ${
                      Array.isArray(formData.injured_persons) && formData.injured_persons.length > 0 ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="Enter number of medical leave days"
                    required
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    {Array.isArray(formData.injured_persons) && formData.injured_persons.length > 0
                      ? 'Auto-calculated as the sum of per-person LTI days below.'
                      : 'Required for statutory reporting compliance'}
                  </p>
                </div>
              </div>
            )}

            {/* Injury & Fatality Counts — shown for relevant incident types,
                directly below the type field (mirrors the LTI block above). */}
            {['Medical Treatment', 'Restricted Work', 'Lost Time Injury', 'Fatality', 'First Aid'].includes(formData.hazard_type) && (
              <div className="border-2 rounded-lg p-4 bg-red-50 border-red-200">
                <h3 className="text-sm font-bold text-red-800 mb-3">Casualty Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="injury_count">
                      Number of Injuries
                    </label>
                    <input
                      id="injury_count"
                      name="injury_count"
                      type="number"
                      min="0"
                      value={formData.injury_count}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                  {formData.hazard_type === 'Fatality' && (
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fatality_count">
                        Number of Fatalities <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="fatality_count"
                        name="fatality_count"
                        type="number"
                        min="1"
                        value={formData.fatality_count}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                        placeholder="1"
                        required
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-2">Used for statutory reporting (TRIR, LTIFR calculations)</p>

                {/* Per-person LTI days — rendered when we have at least one injured person. */}
                {Array.isArray(formData.injured_persons) && formData.injured_persons.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-red-200">
                    <h4 className="text-sm font-bold text-red-800 mb-2">Lost-Time Days per Injured Person</h4>
                    <div className="space-y-2">
                      {formData.injured_persons.map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <label className="text-sm text-gray-700 w-24 shrink-0" htmlFor={`iip-${idx}`}>
                            Person {idx + 1}
                          </label>
                          <input
                            id={`iip-${idx}`}
                            type="number"
                            step="0.5"
                            min="0"
                            value={entry?.lti_days ?? 0}
                            onChange={(e) => handleInjuredPersonChange(idx, e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="LTI days"
                          />
                          <span className="text-xs text-gray-500">days</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Total: {formData.injured_persons.reduce((s, p) => s + (Number(p?.lti_days) || 0), 0)} days
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Incident Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="incident_date">
                  Date of Incident <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="incident-date-input"
                  id="incident_date"
                  name="incident_date"
                  type="date"
                  value={formData.incident_date}
                  onChange={handleInputChange}
                  min="2000-01-01"
                  max={todayLocalISO()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">When did this incident occur?</p>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="incident_time">
                  Time of Incident
                </label>
                <input
                  data-testid="incident-time-input"
                  id="incident_time"
                  name="incident_time"
                  type="time"
                  value={formData.incident_time || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Approximate time is fine. Clear it if unknown.</p>
              </div>
            </div>

            {/* Location — required on Submit (Save Draft uses type="button"
                so native HTML validation is bypassed for drafts). */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="location">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="hazard-location-input"
                id="location"
                name="location"
                type="text"
                required
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="Where did this occur? (e.g., Workshop Floor 2, Warehouse Section B)"
              />
            </div>

            {/* Department (from the tenant department master) */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="department_id">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                id="department_id"
                name="department_id"
                value={formData.department_id}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              >
                <option value="">— Select department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                The incident is routed to this department's Safety Manager.
              </p>
            </div>

            {/* Contractor attribution. Optional and collapsed by default —
                most incidents involve no contractor, and the common case must
                stay frictionless. When set, the incident joins the contractor's
                EHS history and their performance figures. */}
            <div className="md:col-span-2">
              <ContractorPicker
                contractorId={formData.contractor_company_id}
                siteId={activeSiteId}
                showWorker={false}
                onChange={(cid) => setFormData((prev) => ({
                  ...prev, contractor_company_id: cid,
                }))}
                hint="Links this incident to the contractor, so it appears in their EHS history and counts towards their safety performance."
              />
            </div>

            {/* Equipment Involved */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="equipment_involved">
                Equipment Involved
              </label>
              <input
                data-testid="equipment-involved-input"
                id="equipment_involved"
                name="equipment_involved"
                type="text"
                value={formData.equipment_involved}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="Any equipment or machinery involved? (e.g., Forklift, Ladder, Conveyor Belt)"
              />
            </div>

            {/* Immediate Action */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="immediate_actions">
                Immediate Action Taken
              </label>
              <textarea
                data-testid="immediate-action-input"
                id="immediate_actions"
                name="immediate_actions"
                value={formData.immediate_actions}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                placeholder="What immediate actions were taken to address this hazard?"
              />
            </div>

            {/* Assign To - visible to admin / hse_manager / safety_officer */}
            {canAssign && (
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="assignee">
                  Assign To
                </label>
                <select
                  id="assignee"
                  name="assignee"
                  value={formData.assignee}
                  onChange={handleInputChange}
                  disabled={!formData.department_id}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {formData.department_id
                      ? "Auto-assign to the department's Safety Manager"
                      : 'Select a department first'}
                  </option>
                  {/* Keep an already-saved assignee selectable even when they
                      aren't in the department-scoped list, so editing an older
                      incident doesn't silently reassign it. */}
                  {formData.assignee &&
                    !users.some(u => u.name === formData.assignee) && (
                      <option value={formData.assignee}>
                        {formData.assignee} (current)
                      </option>
                    )}
                  {users.map(u => (
                    <option key={u.id} value={u.name}>
                      {u.name}{u.department ? ` — ${u.department}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.department_id && users.length === 0
                    ? 'No Safety Manager is assigned to this department — the incident will go to another Safety Manager.'
                    : "Leave blank to auto-assign to the selected department's Safety Manager."}
                </p>
              </div>
            )}

            {/* Edit-only workflow fields — status, target date, due date.
                Hidden during create because the backend defaults these. */}
            {isEditing && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="status">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                  >
                    {/* CMP-09: closing goes through MARK AS CLOSED on the
                        incident page (approve permission, closure notes), not
                        a plain edit — so Closed is not offered here. */}
                    {allowedTransitions(formData.status)
                      .filter(s => s !== 'Closed' || formData.status === 'Closed')
                      .map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="target_date">
                    Target Date
                  </label>
                  <input
                    id="target_date"
                    name="target_date"
                    type="date"
                    value={formData.target_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="due_date">
                    Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="due_date"
                    name="due_date"
                    type="date"
                    required
                    value={formData.due_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
              </div>
            )}

            {/* Root Cause Category Tags */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3">Root Cause Categories</h3>
              <p className="text-sm text-gray-600 mb-3">Select tags that describe the contributing factors</p>
              <div className="flex flex-wrap gap-2">
                {ROOT_CAUSE_TAGS.map(tag => {
                  const isSelected = formData.tags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          tags: isSelected
                            ? prev.tags.filter(t => t !== tag)
                            : [...prev.tags, tag]
                        }));
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        isSelected
                          ? 'bg-[#1B5C94] text-white border-[#1B5C94]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[#1B5C94] hover:text-[#1B5C94]'
                      }`}
                    >
                      {isSelected ? '✓ ' : ''}{tag}
                    </button>
                  );
                })}
              </div>
              {formData.tags.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">{formData.tags.length} tag(s) selected</p>
              )}
            </div>

            {/* Hidden entirely when the tenant switched off all three fields. */}
            {anyRcaFieldShown && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Root Cause & Actions (Optional)</h3>
              <p className="text-sm text-gray-600 mb-4">💡 Leave blank to auto-generate with AI, or enter your own analysis</p>

              {/* Generate AI Analysis — placed at the top of this section so it
                  sits directly above the fields it populates (Root Cause,
                  Corrective Action, Recommended Actions). Only meaningful for
                  edit mode on a non-Draft, non-Closed incident; the Draft
                  message replaces the button when status is Draft. */}
              {isEditing && loadedStatus && loadedStatus !== 'Draft' && loadedStatus !== 'Closed' && (
                <button
                  type="button"
                  onClick={handleGenerateAIAnalysis}
                  disabled={generatingAI}
                  className="mb-4 inline-flex items-center gap-2 bg-purple-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                  data-testid="generate-ai-analysis-button"
                >
                  {generatingAI ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>🪄 Generate AI Analysis</>
                  )}
                </button>
              )}
              {isEditing && loadedStatus === 'Draft' && (
                <div className="mb-4 inline-block bg-gray-100 border border-gray-300 text-gray-600 text-sm px-4 py-2 rounded-lg">
                  AI analysis is available once the incident is submitted (status moves from Draft to Open).
                </div>
              )}

              {/* Root Cause Analysis */}
              {rcaFields.root_cause && (
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="root_cause">
                  Root Cause Analysis
                </label>
                <textarea
                  id="root_cause"
                  name="root_cause"
                  value={formData.root_cause}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  placeholder="Why did this incident occur? Identify underlying causes... (AI will generate if left blank)"
                />
              </div>
              )}

              {/* Corrective Action */}
              {rcaFields.corrective_action && (
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="corrective_action">
                  Corrective Action
                </label>
                <textarea
                  id="corrective_action"
                  name="corrective_action"
                  value={formData.corrective_action}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  placeholder="Specific steps to prevent recurrence... (AI will generate if left blank)"
                />
              </div>
              )}

              {/* Recommended Actions */}
              {rcaFields.recommended_actions && (
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="recommended_actions">
                  Recommended Future Actions
                </label>
                <textarea
                  id="recommended_actions"
                  name="recommended_actions"
                  value={formData.recommended_actions}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
                  placeholder="Enter preventive measures (separate with semicolons: Action 1; Action 2; Action 3)... (AI will generate if left blank)"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple actions with semicolons (;)</p>
              </div>
              )}
            </div>
            )}

            {/* Attachments — single picker for photos and documents.
                Image files (jpg/jpeg/png/gif) are routed to the photos
                pipeline (S3, 10MB cap, 10-file cap, thumbnail previews);
                everything else goes to the documents pipeline (25MB cap,
                20-file cap). The split is invisible to the user and to
                the form layout — one control to manage all attachments. */}
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="attachments">
                Attachments (Optional)
              </label>

              {/* Loading indicators (edit mode only) */}
              {isEditing && (existingPhotosLoading || existingDocumentsLoading) && (
                <div className="flex items-center mb-3 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#1B5C94] mr-2"></div>
                  Loading existing attachments...
                </div>
              )}

              {/* Existing photos — thumbnail grid */}
              {isEditing && !existingPhotosLoading && existingPhotos.length > 0 && (
                <div className="mb-3" data-testid="existing-photos-grid">
                  <p className="text-sm text-gray-600 mb-2">{existingPhotos.length} photo(s) attached</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {existingPhotos.map((photo, index) => (
                      <div key={photo.object_key || index} className="relative group">
                        <img
                          src={photo.url}
                          alt={`Existing ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg shadow-md cursor-pointer hover:opacity-90"
                          onClick={() => window.open(photo.url, '_blank')}
                        />
                        {photo.object_key && (
                          <button
                            type="button"
                            onClick={() => handleDeleteExistingPhoto(photo.object_key)}
                            disabled={deletingPhotoKey === photo.object_key}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                            title="Delete photo"
                          >
                            {deletingPhotoKey === photo.object_key ? '...' : '×'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Existing documents — list rows */}
              {isEditing && !existingDocumentsLoading && existingDocuments.length > 0 && (
                <div className="mb-3 space-y-2" data-testid="existing-documents-list">
                  <p className="text-sm text-gray-600">{existingDocuments.length} document(s) attached</p>
                  {existingDocuments.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{doc.filename || 'Untitled'}</div>
                        <div className="text-xs text-gray-500">
                          {doc.file_type ? doc.file_type.toUpperCase() : ''}
                          {doc.size_bytes ? ` · ${(doc.size_bytes / 1024).toFixed(0)} KB` : ''}
                          {doc.uploaded_at ? ` · ${String(doc.uploaded_at).substring(0, 10)}` : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingDocument(index)}
                        disabled={deletingDocIndex === index}
                        className="text-red-500 hover:text-red-700 text-sm font-semibold disabled:opacity-50 ml-3"
                      >
                        {deletingDocIndex === index ? '...' : 'Delete'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <input
                data-testid="attachment-upload-input"
                id="attachments"
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                multiple
                onChange={handleAttachmentSelect}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Photos (JPG/PNG/GIF, up to 10) and documents (PDF/Word/Excel/PowerPoint, up to 20). Photos max 10MB, documents max 25MB each.</p>

              {/* Pending photo previews — thumbnail grid */}
              {photoPreviews.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">{photoPreviews.length} photo(s) pending upload</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {photoPreviews.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo.preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg shadow-md"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove"
                        >
                          ×
                        </button>
                        <p className="text-xs text-gray-500 mt-1 truncate">{photo.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending document rows */}
              {documentFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm text-gray-600">{documentFiles.length} document(s) pending upload</p>
                  {documentFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-blue-50 rounded-lg p-2 border border-blue-200">
                      <span className="text-sm text-gray-800 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removePendingDocument(index)}
                        className="text-red-500 hover:text-red-700 text-sm ml-3"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>


            {error && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-alert">
                {error}
              </div>
            )}

            {analyzing && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#1B5C94] mr-3"></div>
                  <span className="text-[#1B5C94] font-semibold">AI is analyzing your report...</span>
                </div>
              </div>
            )}

            {/* Save as Draft Button */}
            <button
              type="button"
              onClick={handleSaveAsDraft}
              disabled={loading || !formData.site_id}
              className="w-full bg-gray-500 text-white font-bold py-4 px-6 rounded-lg hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : '💾 SAVE AS DRAFT'}
            </button>

            {/* Submit Button */}
            <button
              data-testid="submit-hazard-button"
              type="submit"
              disabled={loading || !formData.site_id}
              className="w-full bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-4 px-6 rounded-lg hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isEditing ? 'Submitting...' : 'Submitting & Analyzing...'}
                </span>
              ) : (
                '🚀 SUBMIT INCIDENT REPORT'
              )}
            </button>

            {/* Cancel. In EDIT mode it returns to the record; when creating,
                it returns to the Safety Hub. Previously it appeared only when
                editing, so someone who opened the form and changed their mind
                had no way out from the foot of a long form. */}
            <button
              type="button"
              onClick={() => navigate(isEditing ? `/incidents/${draftId}` : '/safety')}
              disabled={loading}
              className="w-full bg-white text-gray-700 font-bold py-4 px-6 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cancel-edit-button"
            >
              CANCEL
            </button>
          </form>
          )}

          {/* Submit confirmation dialog */}
          {showSubmitConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Submit Incident Report</h3>
                {(() => {
                  // Mirror the status logic in doSubmit (line ~521): Draft is
                  // promoted to Open on submit; any other edit-mode status is
                  // kept as-is (or set to whatever the user picked in the form).
                  // Create mode submits without a status — backend defaults to Open.
                  const targetStatus = !isEditing
                    ? 'Open'
                    : loadedStatus === 'Draft'
                    ? 'Open'
                    : (formData.status || loadedStatus);
                  const isUnchangedEdit =
                    isEditing && loadedStatus !== 'Draft' && targetStatus === loadedStatus;
                  return (
                    <p className="text-gray-600 mb-6">
                      Are you sure you want to submit this incident?{' '}
                      {isUnchangedEdit ? (
                        <>
                          Your changes will be saved with the status remaining{' '}
                          <span className="font-semibold">{loadedStatus}</span>.
                        </>
                      ) : (
                        <>
                          Once submitted, the status will be{' '}
                          <span className="font-semibold">{targetStatus}</span>
                          {' '}and the report will be visible to your safety team.
                        </>
                      )}
                    </p>
                  );
                })()}
                <div className="flex gap-3">
                  <button
                    onClick={doSubmit}
                    className="flex-1 bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-3 px-4 rounded-lg hover:shadow-md transition-all"
                    data-testid="confirm-submit-button"
                  >
                    Yes, Submit
                  </button>
                  <button
                    onClick={() => setShowSubmitConfirm(false)}
                    className="flex-1 bg-white text-gray-700 font-bold py-3 px-4 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                    data-testid="cancel-submit-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
      </div>
  );
};

export default ReportHazard;